import { toDocSearchParams } from '@affine/core/modules/navigation';
import {
  type AggregateInput,
  type IndexerAggregateQuery,
  indexerAggregateQuery,
  type IndexerSearchDocsQuery,
  indexerSearchDocsQuery,
  type MailboxRootQuery,
  mailboxRootQuery,
  SearchTable,
} from '@affine/graphql';
import type {
  IndexerPreferOptions,
  IndexerSyncState,
  Query,
} from '@affine/nbstore';
import type { ReferenceParams } from '@blocksuite/affine/model';
import { fromPromise, LiveData, Service } from '@toeverything/infra';
import { isEmpty, omit } from 'lodash-es';
import { from, map, type Observable, of, switchMap, throwError } from 'rxjs';
import { z } from 'zod';

import type { WorkspaceServerService } from '../../cloud';
import type { DocsService } from '../../doc/services/docs';
import type { WorkspaceService } from '../../workspace';
import {
  createDocsSearchAggregateOptions,
  createServerAggregateQuery,
  mapDocsSearchAggregateBuckets,
  mapLocalDocTitleMatches,
  mergeDocsSearchAggregateItems,
} from './docs-search-aggregate';
import {
  mapRemoteSearchDocsForQuickSearch,
  mergeRemoteDocsSearchResults,
} from './docs-search-remote';
import type { DocsSearchResult } from './source-navigation';

export interface DocsSearchOptions {
  readonly prefer?: IndexerPreferOptions;
  readonly includeTechnicalSources?: boolean;
}

type HiddenTechnicalDocIds = ReadonlySet<string> | null;

export function filterHiddenTechnicalDocs<T extends { docId: string }>(
  items: T[],
  hiddenTechnicalDocIds: HiddenTechnicalDocIds,
  options: DocsSearchOptions = {}
): T[] {
  if (options.includeTechnicalSources) return items;
  if (hiddenTechnicalDocIds === null) return [];
  if (hiddenTechnicalDocIds.size === 0) return items;
  return items.filter(item => !hiddenTechnicalDocIds.has(item.docId));
}

export class DocsSearchService extends Service {
  private hiddenTechnicalDocIdsPromise: Promise<HiddenTechnicalDocIds> | null =
    null;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docsService: DocsService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  get indexer() {
    return this.workspaceService.workspace.engine.indexer;
  }

  readonly indexerState$ = LiveData.from(this.indexer.state$, {
    indexing: 0,
    errorMessage: null,
  } as IndexerSyncState);

  searchTitle$(query: string) {
    return this.indexer
      .search$(
        'doc',
        {
          type: 'match',
          field: 'title',
          match: query,
        },
        {
          pagination: {
            skip: 0,
            limit: Infinity,
          },
        }
      )
      .pipe(
        map(({ nodes }: { nodes: { id: string }[] }) => {
          return nodes.map((node: { id: string }) => node.id);
        })
      );
  }

  search$(
    query: string,
    options: DocsSearchOptions = {}
  ): Observable<DocsSearchResult[]> {
    if (options.prefer === 'remote') {
      return this.searchRemote$(query, options);
    }

    const hiddenTechnicalDocIds$ = options.includeTechnicalSources
      ? of(new Set<string>() as HiddenTechnicalDocIds)
      : from(this.getDefaultHiddenTechnicalDocIds());

    return hiddenTechnicalDocIds$.pipe(
      switchMap(hiddenTechnicalDocIds =>
        this.indexer
          .aggregate$(
            'block',
            this.createLocalSearchQuery(query),
            'docId',
            createDocsSearchAggregateOptions()
          )
          .pipe(
            map(({ buckets }) => {
              const indexedResults = mapDocsSearchAggregateBuckets(
                buckets,
                query,
                this.getDocTitle
              );
              return filterHiddenTechnicalDocs(
                mergeDocsSearchAggregateItems(
                  this.getLocalDocTitleMatches(query),
                  indexedResults
                ),
                hiddenTechnicalDocIds,
                options
              );
            })
          )
      )
    );
  }

  private searchRemote$(query: string, options: DocsSearchOptions = {}) {
    const server = this.workspaceServerService.server;
    if (!server) {
      return throwError(() => new Error('Workspace server is not bound'));
    }

    const input: AggregateInput = {
      table: SearchTable.block,
      query: createServerAggregateQuery(query),
      field: 'docId',
      options: {
        ...createDocsSearchAggregateOptions(),
        includeTechnicalSources: options.includeTechnicalSources === true,
      },
    };

    const workspaceId = this.workspaceService.workspace.id;

    return from(
      Promise.all([
        server.gql({
          query: indexerAggregateQuery,
          variables: {
            id: workspaceId,
            input,
          },
        }) as Promise<IndexerAggregateQuery>,
        server.gql({
          query: indexerSearchDocsQuery,
          variables: {
            id: workspaceId,
            input: {
              keyword: query,
              limit: 50,
              includeTechnicalSources: options.includeTechnicalSources === true,
            },
          },
        }) as Promise<IndexerSearchDocsQuery>,
      ])
    ).pipe(
      map(([aggregateResult, searchDocsResult]) =>
        mergeRemoteDocsSearchResults(
          mapDocsSearchAggregateBuckets(
            aggregateResult.workspace.aggregate.buckets,
            query,
            this.getDocTitle
          ),
          mapRemoteSearchDocsForQuickSearch(
            searchDocsResult.workspace.searchDocs,
            query
          )
        )
      )
    );
  }

  private createLocalSearchQuery(query: string): Query<'block'> {
    return {
      type: 'boolean',
      occur: 'must',
      queries: [
        {
          type: 'match',
          field: 'content',
          match: query,
        },
        {
          type: 'boolean',
          occur: 'should',
          queries: [
            {
              type: 'match',
              field: 'content',
              match: query,
            },
            {
              type: 'boost',
              boost: 1.5,
              query: {
                type: 'match',
                field: 'flavour',
                match: 'affine:page',
              },
            },
          ],
        },
      ],
    };
  }

  private readonly getDocTitle = (docId: string): string =>
    this.docsService.list.doc$(docId).value?.title$.value ?? '';

  private readonly getLocalDocTitleMatches = (query: string) =>
    mapLocalDocTitleMatches(
      query,
      this.docsService.list.docs$.value.map(doc => ({
        docId: doc.id,
        title: doc.title$.value,
        trash: doc.trash$.value,
      }))
    );

  private async getDefaultHiddenTechnicalDocIds(): Promise<HiddenTechnicalDocIds> {
    const server = this.workspaceServerService.server;
    if (!server) return new Set();

    this.hiddenTechnicalDocIdsPromise ??= (
      server.gql({
        query: mailboxRootQuery,
        variables: { workspaceId: this.workspaceService.workspace.id },
      }) as Promise<MailboxRootQuery>
    )
      .then((result: MailboxRootQuery) => {
        const docId = result.mailboxRoot?.docId;
        return docId ? new Set([docId]) : new Set<string>();
      })
      .catch(error => {
        console.warn(
          '[DocsSearchService] Failed to resolve hidden technical docs for local search',
          error
        );
        return new Set<string>();
      });

    return this.hiddenTechnicalDocIdsPromise;
  }

  watchRefsFrom(ids: string | string[]) {
    const docIds = Array.isArray(ids) ? ids : [ids];
    if (docIds.length === 0) {
      return of([]);
    }

    return this.indexer
      .search$(
        'block',
        {
          type: 'boolean',
          occur: 'must',
          queries: [
            {
              type: 'boolean',
              occur: 'should',
              queries: docIds.map(id => ({
                type: 'match',
                field: 'docId',
                match: id,
              })),
            },
            {
              type: 'exists',
              field: 'refDocId',
            },
          ],
        },
        {
          fields: ['refDocId', 'ref'],
          pagination: {
            limit: 100,
          },
        }
      )
      .pipe(
        switchMap(({ nodes }) => {
          return fromPromise(async () => {
            const refs = Array.from(
              new Map(
                nodes
                  .flatMap((node: { fields: { ref?: string | string[] } }) => {
                    const { ref } = node.fields;
                    return typeof ref === 'string'
                      ? [JSON.parse(ref)]
                      : (ref?.map((item: string) => JSON.parse(item)) ?? []);
                  })
                  .filter(
                    (ref: { docId: string }) => !docIds.includes(ref.docId)
                  )
                  .map((ref: { docId: string }) => [ref.docId, ref] as const)
              ).values()
            ) as ({ docId: string } & ReferenceParams)[];

            return refs
              .flatMap((ref: { docId: string }) => {
                const doc = this.docsService.list.doc$(ref.docId).value;
                if (!doc) return null;

                const title = doc.title$.value;
                const params = omit(ref, ['docId']);

                return {
                  title,
                  docId: doc.id,
                  params: isEmpty(params)
                    ? undefined
                    : toDocSearchParams(params),
                };
              })
              .filter(ref => !!ref);
          });
        })
      );
  }

  watchDatabasesTo(docId: string) {
    const DatabaseAdditionalSchema = z.object({
      databaseName: z.string().optional(),
    });
    return this.indexer
      .search$(
        'block',
        {
          type: 'boolean',
          occur: 'must',
          queries: [
            {
              type: 'match',
              field: 'refDocId',
              match: docId,
            },
            {
              type: 'match',
              field: 'parentFlavour',
              match: 'affine:database',
            },
          ],
        },
        {
          fields: ['docId', 'blockId', 'parentBlockId', 'additional'],
          pagination: {
            limit: 100,
          },
        }
      )
      .pipe(
        map(({ nodes }) => {
          return nodes
            .map((node: { fields: Record<string, string | string[]> }) => {
              if (node.fields.docId === docId) {
                // Ignore if it is a link to the current document.
                return null;
              }

              const additional =
                typeof node.fields.additional === 'string'
                  ? node.fields.additional
                  : node.fields.additional[0];

              return {
                docId:
                  typeof node.fields.docId === 'string'
                    ? node.fields.docId
                    : node.fields.docId[0],
                rowId:
                  typeof node.fields.blockId === 'string'
                    ? node.fields.blockId
                    : node.fields.blockId[0],
                databaseBlockId:
                  typeof node.fields.parentBlockId === 'string'
                    ? node.fields.parentBlockId
                    : node.fields.parentBlockId[0],
                databaseName: DatabaseAdditionalSchema.safeParse(additional)
                  .data?.databaseName as string | undefined,
              };
            })
            .filter(
              (item: unknown): item is NonNullable<typeof item> => item !== null
            );
        })
      );
  }

  watchDocSummary(docId: string) {
    return this.indexer
      .search$(
        'doc',
        {
          type: 'match',
          field: 'docId',
          match: docId,
        },
        {
          fields: ['summary'],
          pagination: {
            limit: 1,
          },
        }
      )
      .pipe(
        map(({ nodes }) => {
          const node = nodes.at(0);
          return (
            (typeof node?.fields.summary === 'string'
              ? node?.fields.summary
              : node?.fields.summary[0]) ?? null
          );
        })
      );
  }
}
